import { motion, AnimatePresence } from 'framer-motion';
import { useRef, useState } from 'react';
import { X, ExternalLink, TrendingUp } from 'lucide-react';
import { portfolioProjects } from '../data/mock';

const categories = ['All', 'Web Development', 'Digital Marketing', 'Social Media', 'Video Editing'];

export default function PortfolioPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const ref = useRef(null);

  const filtered =
    activeCategory === 'All'
      ? portfolioProjects
      : portfolioProjects.filter((p) => p.category === activeCategory);

  return (
    <main className="w-full max-w-full overflow-x-hidden">
      {/* Page Hero */}
      <section className="relative overflow-hidden pt-32 sm:pt-36 pb-14 sm:pb-16">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[320px] sm:w-[500px] h-[280px] sm:h-[400px] bg-blue-600/6 rounded-full blur-[100px] sm:blur-[120px]" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3 block">
              Portfolio
            </span>

            <h1 className="text-[2.5rem] sm:text-5xl lg:text-6xl font-bold font-['Syne'] text-[var(--text-primary)] mb-6 leading-tight break-words">
              Our <span className="text-blue-400">Work</span> Speaks
              <br />
              For Itself
            </h1>

            <p className="text-base sm:text-lg text-[var(--text-muted)] max-w-2xl leading-relaxed">
              Explore our portfolio of successful projects across web development, digital marketing, and social media management.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="relative pb-8 overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap gap-3 overflow-hidden"
          >
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${activeCategory === cat
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                    : 'bg-[var(--surface-2)] text-[var(--text-muted)] border border-[var(--border-color)] hover:text-[var(--text-primary)] hover:border-slate-600'
                  }`}
              >
                {cat}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section ref={ref} className="relative py-8 pb-24 overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <AnimatePresence mode="popLayout">
              {filtered.map((project, index) => (
                <motion.div
                  layout
                  key={project.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  onClick={() => setSelectedProject(project)}
                  className="group cursor-pointer rounded-2xl overflow-hidden bg-[var(--card-color)] border border-[var(--border-color)] hover:border-blue-500/20 transition-all duration-500 min-w-0"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--card-color)] via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 max-w-[calc(100%-2rem)]">
                      <span className="text-xs px-3 py-1.5 rounded-full bg-blue-600/80 text-white font-medium backdrop-blur-sm break-words">
                        {project.category}
                      </span>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center">
                        <ExternalLink className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="p-6 min-w-0">
                    <h3 className="text-lg font-bold font-['Syne'] text-[var(--text-primary)] mb-2 break-words">
                      {project.title}
                    </h3>

                    <p className="text-[var(--text-muted)] text-sm leading-relaxed mb-4 line-clamp-2 break-words">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2.5 py-1 rounded-md bg-[var(--surface-2)] text-[var(--text-muted)] border border-[var(--border-color)] break-words"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[var(--text-muted)] text-lg">
                No projects found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Case Study Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setSelectedProject(null)}
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: 'spring', damping: 25 }}
              className="relative w-full max-w-2xl bg-[var(--card-color)] border border-[var(--border-color)] rounded-2xl overflow-hidden max-h-[85vh] overflow-y-auto"
            >
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors duration-300"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative h-64 overflow-hidden">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--card-color)] via-[var(--card-color)]/40 to-transparent" />
              </div>

              <div className="p-6 sm:p-8 -mt-12 relative min-w-0">
                <span className="text-xs px-3 py-1.5 rounded-full bg-blue-600/80 text-white font-medium mb-4 inline-block break-words">
                  {selectedProject.category}
                </span>

                <h2 className="text-2xl md:text-3xl font-bold font-['Syne'] text-[var(--text-primary)] mb-3 break-words">
                  {selectedProject.title}
                </h2>

                <p className="text-[var(--text-muted)] leading-relaxed mb-6 break-words">
                  {selectedProject.description}
                </p>

                <h4 className="text-[var(--text-primary)] font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  Key Results
                </h4>

                <div className="space-y-3 mb-6">
                  {selectedProject.results.map((result) => (
                    <div key={result} className="flex items-center gap-3 text-sm">
                      <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
                      <span className="text-[var(--text-secondary)] break-words">{result}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2">
                  {selectedProject.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1.5 rounded-full bg-[var(--surface-2)] text-[var(--text-muted)] border border-[var(--border-color)] break-words"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}